import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { OverlayPanel } from 'primereact/overlaypanel';
import CPTEC, { CPTEC_Forecast, CPTEC_Prediction } from '../../api/CPTEC';
import OWM, { OWM_Data } from '../../api/OpenWeatherMap';
import { WeatherChart } from './WeatherChart';
import { Button } from 'primereact/button';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';

type Props = {
  lat: number;
  lon: number;
};

export const WeatherForecastWidget: React.FC<Props> = (props: Props) => {
  const op = useRef<OverlayPanel>(null);
  const [selectedDay, setSelectedDay] = useState<CPTEC_Prediction | null>(null);
  const [cptecData, setCPTECData] = useState<CPTEC_Forecast | null>(null);
  const [owmData, setOWMData] = useState<OWM_Data | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 10 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const fetchcptecData = async () => {
    try {
      const data = await CPTEC.previsao(props.lat, props.lon);
      setCPTECData(data);
      // console.log('Forecast data fetched:', data);
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      setCPTECData(null);
    }
  };

  useEffect(() => {
    if (!props.lat || !props.lon) return;
    // console.log(`Fetching weather data for lat: ${props.lat}, lon: ${props.lon}`);
    fetchcptecData();
  }, [props.lat, props.lon]);

  const showDetails = async (event: React.MouseEvent, day: CPTEC_Prediction) => {
    setSelectedDay(day);
    const data = await OWM.getWeatherData(props.lat, props.lon);
    const selectedDate = new Date(day.dia);
    const filteredOwmData = {
      ...data,
      list: data.list.filter(item => {
        const itemDate = new Date(item.dt * 1000);
        return itemDate.toDateString() === selectedDate.toDateString();
      })
    };
    setOWMData(filteredOwmData);
    op.current?.toggle(event);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const toggleMinimized = () => {
    setMinimized(!minimized);
  };

  const header = (
    <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span>Previsão do Tempo</span>
        <h5 style={{ fontSize: 12, marginTop: -0}}>{cptecData?.nome}/{cptecData?.uf}</h5>
      </div>
      <Button icon={minimized ? "pi pi-window-maximize" : "pi pi-window-minimize"} className="p-button-rounded p-button-text" onClick={toggleMinimized} />
    </div>
  );

  const handleRowClick = (e: DataTableRowClickEvent) => {
    showDetails(e.originalEvent as unknown as React.MouseEvent, e.data as CPTEC_Prediction);
  };

  const dayBodyTemplate = (rowData: CPTEC_Prediction) => {
    return (
        <div style={{ fontWeight: CPTEC.isToday(new Date(rowData.dia)) ? 'bold' : 'normal' }}>
            {CPTEC.dia(new Date(rowData.dia))}
        </div>
    );
  }

  const maxBodyTemplate = (rowData: CPTEC_Prediction) => `${rowData.maxima}°C`;
  const minBodyTemplate = (rowData: CPTEC_Prediction) => `${rowData.minima}°C`;

  const conditionBodyTemplate = (rowData: CPTEC_Prediction) => {
      return <img src={CPTEC.getWeatherIcon(rowData.tempo)} alt={CPTEC.CPTEC_Description[rowData.tempo]} style={{ width: '30px', height: '30px' }} />;
  };

  // const rowStyle = (data: CPTEC_Prediction) => {
  //   if (CPTEC.isToday(new Date(data.dia))) {
  //       return { backgroundColor: '#e0f7fa' };
  //   }
  //   return {};
  // };
  const rowStyle = (data: CPTEC_Prediction) => {
    return {
            'bg-primary': CPTEC.isToday(new Date(data.dia))
        };
  };

  return (
    <Card title={header} className="weather-widget" style={{ width: '300px', position: 'absolute', top: `${position.y}px`, left: `${position.x}px`, zIndex: 1000, ...minimized && {height:120}}}>
      {!minimized && (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: -30, marginBottom: -30 }}>
            {cptecData?.previsao.map((previsao, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '5px 0',
                  borderBottom: index < cptecData.previsao.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: CPTEC.isToday(new Date(previsao.dia)) ? '#e0f7fa' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={(e) => showDetails(e, previsao)}
              >
                <div style={{ marginLeft: 10, fontWeight: CPTEC.isToday(new Date(previsao.dia)) ? 'bold' : 'normal' }}>{CPTEC.dia(new Date(previsao.dia))}</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '10px' }}>
                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: 50}}><div style={{width: 15, fontSize:7}}>▲</div>{previsao.maxima}°C</div>
                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: 50}}><div style={{width: 15, fontSize:7}}>▼</div>{previsao.minima}°C</div>
                  </div>
                  <img src={CPTEC.getWeatherIcon(previsao.tempo)} alt="Weather Icon" style={{ width: '30px', height: '30px', marginRight: '5px' }} />
                </div>
              </div>
            ))}
          </div>
          {/* <div style={{ marginTop: -20, marginBottom: -20 }}>
            <DataTable value={cptecData?.previsao} onRowClick={handleRowClick} size="small" rowClassName={rowStyle} style={{ cursor: 'pointer' }}>
                <Column body={dayBodyTemplate} header="Dia"></Column>
                <Column body={maxBodyTemplate} header="Máx"></Column>
                <Column body={minBodyTemplate} header="Mín"></Column>
                <Column body={conditionBodyTemplate} header=""></Column>
            </DataTable>
          </div> */}

          <OverlayPanel ref={op} >
            {selectedDay && (
              <div style={{ padding: '10px' }}>
                <h2>{cptecData?.nome}</h2>
                <h4 style={{ marginTop: -16 }}>{new Date(selectedDay?.dia).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })} </h4>
                <p>Máxima: {selectedDay?.maxima}°C</p>
                <p>Mínima: {selectedDay?.minima}°C</p>
                <p>Condição: {CPTEC.CPTEC_Description[selectedDay?.tempo]}</p>
                <img src={CPTEC.getWeatherIcon(selectedDay?.tempo)} alt="Weather Icon" style={{ width: '50px', height: '50px' }} />
                {
                  owmData && (
                    <WeatherChart owmData={owmData} />
                  )
                }
              </div>
            )}
          </OverlayPanel>
        </>
      )}
    </Card>
  );
};
